import { Page } from '@/pages/AdvancedEditor/AdvancedEditor';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { parseRawStore } from '@/services/polotno-parser/polotno-parser.service';
import { IProjectTemplate } from '@d-id/types';
import { nanoid } from 'nanoid';
import { StoreType } from 'polotno/model/store';

// todo: those values should be fetched from the brand kit
const DEFAULT_BRAND_KIT = {
    fonts: {
        primary: 'Inter',
        secondary: 'Inter',
        tertiary: 'Inter',
    },
    colors: {
        primary: '#0000aa',
        secondary: '#0000aa',
        tertiary: '#0000aa',
    },
};

const getValuesToReplace = (store: StoreType, brandKitEnabled?: boolean) => {
    if (!brandKitEnabled) {
        return {
            fonts: store.custom.fonts,
            colors: store.custom.colors,
        };
    }

    return {
        fonts: {
            primary: DEFAULT_BRAND_KIT.fonts.primary || store.custom.fonts.primary,
            secondary: DEFAULT_BRAND_KIT.fonts.secondary || store.custom.fonts.secondary,
            tertiary: DEFAULT_BRAND_KIT.fonts.tertiary || store.custom.fonts.tertiary,
        },
        colors: {
            primary: DEFAULT_BRAND_KIT.colors.primary || store.custom.colors.primary,
            secondary: DEFAULT_BRAND_KIT.colors.secondary || store.custom.colors.secondary,
            tertiary: DEFAULT_BRAND_KIT.colors.tertiary || store.custom.colors.tertiary,
        },
    };
};

export const fetchTemplateRawData = async (template: IProjectTemplate) => {
    const response = await fetch(template.rawDataUrl);
    const data = await response.json();

    const parsedStore = parseRawStore(data);
    return parsedStore;
};

export const parsePageData = (rawStore: StoreType, page: Page, brandKitEnabled?: boolean) => {
    const stringifiedPage = JSON.stringify(page);

    const valuesToReplace = getValuesToReplace(rawStore, brandKitEnabled);

    const stringifiedPageWithColors = stringifiedPage
        .replaceAll('primary-color', valuesToReplace.colors.primary)
        .replaceAll('secondary-color', valuesToReplace.colors.secondary)
        .replaceAll('tertiary-color', valuesToReplace.colors.tertiary);
    const stringifiedPageWithFont = stringifiedPageWithColors
        .replaceAll('primary-font', valuesToReplace.fonts.primary)
        .replaceAll('secondary-font', valuesToReplace.fonts.secondary)
        .replaceAll('tertiary-font', valuesToReplace.fonts.tertiary);

    const parsedPage = JSON.parse(stringifiedPageWithFont);

    return parsedPage;
};

export const useApplyTemplate = () => {
    const { store } = useAdvancedEditorContext();
    const brandKitEnabled = useFlag('projects_brandkit_enabled', false);

    // if targetPageId is not provided, template will be applied to new page
    const applyTemplate = async (template: StoreType) => {
        try {
            store.history.transaction(() => {
                store.deletePages(store.pages.map(page => page.id));

                for (const page of template.pages) {
                    const parsedPage = parsePageData(template, page, brandKitEnabled);

                    store.addPage({ id: nanoid(), ...parsedPage });
                }

                store.selectPage(store.pages[0].id);
            });
        } catch (error) {
            logger.error('Error during applying template data:', error);
        }
    };

    const applyScene = async (template: StoreType, pageIndex: number, targetPageId?: string) => {
        try {
            const pageToApply = template.pages[pageIndex];

            const parsedPage = parsePageData(template, pageToApply, brandKitEnabled);

            if (targetPageId) {
                const targetPage = store.pages.find(page => page.id === targetPageId);
                const { id, ...pageWithoutId } = parsedPage;

                if (targetPage) {
                    targetPage.set(pageWithoutId);
                }
                return;
            }

            store.addPage({ id: nanoid(), ...parsedPage });
        } catch (error) {
            logger.error('Error during applying scene data:', error);
        }
    };

    return { applyScene, applyTemplate };
};
